import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { AuthProvider, useAuth } from "./auth";
import { BottomNav, ScrollRestorer, StatusBanners } from "./components/Layout";
import { ConfirmProvider, Skeleton, ToastProvider } from "./components/ui";
import { createQueryClient } from "./lib/queries";
import { HomePage } from "./pages/Home";
import { InvitationPage, LoginPage } from "./pages/Login";
import { PasswordForgotPage } from "./pages/PasswordForgot";
import { SignupPage } from "./pages/Signup";
import { TokenLinkPage } from "./pages/TokenLink";
import { PlanningPage } from "./pages/Planning";
import { useKeyboardAware } from "./lib/hooks";

const InterventionPage = lazy(() => import("./pages/InterventionDetail"));
const InterventionFormPage = lazy(() => import("./pages/InterventionForm"));
const ClientsPage = lazy(() => import("./pages/Clients"));
const ClientPage = lazy(() => import("./pages/ClientDetail"));
const ClientFormPage = lazy(() => import("./pages/ClientForm"));
const ContractPage = lazy(() => import("./pages/ContractDetail"));
const ContractFormPage = lazy(() => import("./pages/ContractForm"));
const MaintenancePage = lazy(() => import("./pages/Maintenance"));
const MorePage = lazy(() => import("./pages/More"));
const TodosPage = lazy(() => import("./pages/Todos"));
const OnCallPage = lazy(() => import("./pages/OnCall"));
const TeamPage = lazy(() => import("./pages/Team"));
const AccountPage = lazy(() => import("./pages/Account"));
const AuditPage = lazy(() => import("./pages/Audit"));
const InvoicingPage = lazy(() => import("./pages/Invoicing"));
const EquipmentTypesPage = lazy(() => import("./pages/EquipmentTypes"));
const CompanyPage = lazy(() => import("./pages/Company"));

const queryClient = createQueryClient();
const persister = createSyncStoragePersister({
  storage: typeof window !== "undefined" ? window.localStorage : undefined,
  key: "tp:cache",
  throttleTime: 2000
});

/** Pages accessibles après connexion, avec la barre de navigation. */
function Protected({ withNav = true }: { withNav?: boolean }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="page">
        <Skeleton count={4} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return (
    <>
      <Suspense
        fallback={
          <div className="page" style={{ paddingTop: 72 }}>
            <Skeleton count={4} />
          </div>
        }
      >
        <Outlet />
      </Suspense>
      {withNav && <BottomNav />}
    </>
  );
}

function AdminOnly({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  return user?.role === "admin" ? <>{children}</> : <Navigate to="/" replace />;
}

function useServiceWorker() {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [update, setUpdate] = useState<() => void>(() => () => window.location.reload());
  useEffect(() => {
    if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;
    void import("virtual:pwa-register").then(({ registerSW }) => {
      const updateSW = registerSW({
        onNeedRefresh: () => setNeedRefresh(true),
        onRegisteredSW(_url, registration) {
          if (!registration) return;
          // Application installée sur le téléphone : elle n'est presque jamais rechargée, elle revient
          // simplement au premier plan. On cherche donc une nouvelle version à chaque retour dans
          // l'application, au retour du réseau, et toutes les 15 minutes si elle reste ouverte.
          let last = 0;
          const check = () => {
            if (document.visibilityState !== "visible" || !navigator.onLine || Date.now() - last < 30_000) return;
            last = Date.now();
            void registration.update().catch(() => undefined);
          };
          document.addEventListener("visibilitychange", check);
          window.addEventListener("focus", check);
          window.addEventListener("online", check);
          setInterval(check, 15 * 60 * 1000);
        }
      });
      setUpdate(() => () => void updateSW(true));
    });
  }, []);
  return { needRefresh, update };
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/mot-de-passe-oublie" element={<PasswordForgotPage />} />
      <Route path="/lien/:token" element={<TokenLinkPage />} />
      <Route path="/s/:code" element={<InvitationPage />} />
      <Route path="/inscription" element={<SignupPage />} />
      <Route element={<Protected />}>
        <Route index element={<HomePage />} />
        <Route path="planning" element={<PlanningPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="clients/:id" element={<ClientPage />} />
        <Route path="maintenance" element={<MaintenancePage />} />
        <Route path="more" element={<MorePage />} />
        <Route path="todos" element={<TodosPage />} />
        <Route path="on-call" element={<OnCallPage />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="contracts/:id" element={<ContractPage />} />
        <Route path="team" element={<AdminOnly><TeamPage /></AdminOnly>} />
        <Route path="audit" element={<AdminOnly><AuditPage /></AdminOnly>} />
        <Route path="company" element={<AdminOnly><CompanyPage /></AdminOnly>} />
        <Route path="invoicing" element={<InvoicingPage />} />
        <Route path="equipment-types" element={<EquipmentTypesPage />} />
      </Route>
      {/* Écrans de saisie : plein écran, sans barre de navigation. */}
      <Route element={<Protected withNav={false} />}>
        <Route path="interventions/new" element={<InterventionFormPage />} />
        <Route path="interventions/:id" element={<InterventionPage />} />
        <Route path="interventions/:id/edit" element={<InterventionFormPage />} />
        <Route path="clients/new" element={<ClientFormPage />} />
        <Route path="clients/:id/edit" element={<ClientFormPage />} />
        <Route path="contracts/new" element={<ContractFormPage />} />
        <Route path="contracts/:id/edit" element={<ContractFormPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function Shell() {
  const { needRefresh, update } = useServiceWorker();
  useKeyboardAware();
  return (
    <div className="app">
      <StatusBanners needRefresh={needRefresh} onUpdate={update} />
      <ScrollRestorer />
      <AppRoutes />
    </div>
  );
}

export function App() {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 1000 * 60 * 60 * 24 * 7,
        buster: "v1",
        // La session n'est jamais restaurée depuis le cache : elle est revérifiée.
        dehydrateOptions: { shouldDehydrateQuery: (q) => q.queryKey[0] !== "session" && q.state.status === "success" }
      }}
    >
      <BrowserRouter>
        <ToastProvider>
          <ConfirmProvider>
            <AuthProvider>
              <Shell />
            </AuthProvider>
          </ConfirmProvider>
        </ToastProvider>
      </BrowserRouter>
    </PersistQueryClientProvider>
  );
}
